import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  /** Puntuación en escala 0-10 (se representa como 5 estrellas, cada una vale 2 puntos) */
  value: number;
  /** Si se define, la calificación se vuelve interactiva */
  onChange?: (value: number) => void;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses: Record<NonNullable<StarRatingProps["size"]>, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-7 w-7",
};

export function StarRating({
  value,
  onChange,
  size = "md",
  className,
}: StarRatingProps) {
  const isInteractive = !!onChange;
  const starClass = sizeClasses[size];

  const handleSelect = (starIndex: number, half: boolean) => {
    if (!onChange) return;
    const points = starIndex * 2 - (half ? 1 : 0);
    onChange(points);
  };

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {[1, 2, 3, 4, 5].map((starIndex) => {
        const fullThreshold = starIndex * 2;
        const halfThreshold = starIndex * 2 - 1;
        const fillPercent =
          value >= fullThreshold
            ? 100
            : value >= halfThreshold
              ? 50
              : 0;

        return (
          <div
            key={starIndex}
            className={cn(
              "relative",
              starClass,
              isInteractive && "cursor-pointer",
            )}
          >
            <Star
              className={cn(starClass, "text-muted-foreground/30")}
              strokeWidth={1.5}
            />
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fillPercent}%` }}
            >
              <Star
                className={cn(starClass, "fill-yellow-400 text-yellow-400")}
                strokeWidth={1.5}
              />
            </div>

            {isInteractive && (
              <>
                <button
                  type="button"
                  aria-label={`${halfThreshold} puntos`}
                  className="absolute inset-y-0 left-0 w-1/2"
                  onClick={() => handleSelect(starIndex, true)}
                />
                <button
                  type="button"
                  aria-label={`${fullThreshold} puntos`}
                  className="absolute inset-y-0 right-0 w-1/2"
                  onClick={() => handleSelect(starIndex, false)}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
